import {
  FC,
  useRef,
  useState,
  useCallback,
  ChangeEvent,
  DragEvent,
} from 'react';
import { useDispatch, useSelector, Provider } from 'react-redux';
import { I18nextProvider } from 'react-i18next';

import alertTypes from 'constants/alertTypes';
import { State } from 'redux/initialState';
import { store } from 'redux/store';
import { addAlert } from 'modules/_shared/redux/alertActions';
import InputCheckbox from 'modules/_shared/components/InputCheckbox';
import IconTooltip from 'modules/_shared/components/Icon/IconTooltip';
import InputText from 'modules/_shared/components/InputText';
import { MessageType } from 'modules/_shared/components/InputMessage';
import MandatoryField from 'modules/_shared/components/MandatoryField';
import { useTranslate } from 'hooks/useTranslate';
import i18n from 'i18n/config';

import validateIBAN from 'modules/trading/utils/validateIBAN';
import EquityMarketTermsByDefault from 'modules/trading/pages/EquityMarketTerms/components/EquityMarketTermsByDefault';
import EquityMarketTermsByTemplate from 'modules/trading/pages/EquityMarketTerms/components/EquityMarketTermsByTemplate';

import { usePublishTradingOfferModal } from '../context/PublishTradingOfferModalContext';

const ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png';

const PublishTradingOfferModalStep2: FC = () => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const { data, actions } = usePublishTradingOfferModal();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const actualSociety = useSelector(
    ({ society }: State) => society?.actualSociety
  );

  const [ibanError, setIbanError] = useState('');

  const handleShowTerms = (e: { preventDefault(): void }) => {
    e.preventDefault();
    const templateId = actualSociety?.templates?.equityMarketTermsAndConditions;

    dispatch(
      addAlert({
        type: alertTypes.NONE,
        title: `<h5 class="modal-title">${i18n.t(
          'OfferConditionsPageTitle'
        )}</h5>`,
        html: (
          <Provider store={store}>
            <I18nextProvider i18n={i18n}>
              {templateId ? (
                <EquityMarketTermsByTemplate templateId={templateId} />
              ) : (
                <EquityMarketTermsByDefault />
              )}
            </I18nextProvider>
          </Provider>
        ),
        props: {
          width: 'min(1200px, 95vw)',
          confirmButtonText: i18n.t('Close'),
          onOpen: (popup: HTMLElement) => {
            const htmlContainer = popup.querySelector(
              '.swal2-html-container'
            ) as HTMLElement | null;
            if (htmlContainer) {
              htmlContainer.style.margin = '0';
              htmlContainer.style.padding = '0';
            }
          },
        },
      })
    );
  };

  const partnerAddress = data.publisherPartner?.address;
  const isSellOffer = data.tradingOffer?.type === 'SELL';

  const handleBankAccountChange = useCallback(
    (value: string) => {
      actions.handleUpdatePublisherPartnerField('bankAccountNumber', value);
      if (ibanError) {
        setIbanError('');
      }
    },
    [actions, ibanError]
  );

  const handleBankAccountBlur = useCallback(() => {
    const bankAccount = data.publisherPartner?.bankAccountNumber || '';
    if (!bankAccount) {
      setIbanError('');
      return;
    }
    const { isValid, error } = validateIBAN(bankAccount);
    setIbanError(isValid ? '' : error || '');
  }, [data.publisherPartner?.bankAccountNumber]);

  const handleFileSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files) {
        Array.from(files).forEach((file) =>
          actions.handleAddPublisherDocument(file)
        );
      }
      e.target.value = '';
    },
    [actions]
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const { files } = e.dataTransfer;
      if (files) {
        Array.from(files).forEach((file) =>
          actions.handleAddPublisherDocument(file)
        );
      }
    },
    [actions]
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className="nk-stepper-step active">
      <div className="text-center mb-2">
        <em className="icon ni ni-shield-check fs-24px text-primary" />
        <h5 className="mt-2">{t('DataVerification')}</h5>
        <p className="text-soft">{t('ConfirmYourDataToCompleteOperation')}</p>
      </div>

      <div className="row">
        <div className="col-12 my-2">
          <h6 className="overline-title text-soft">{t('PersonalData')}</h6>
        </div>

        <div className="col-md-6 mb-3">
          <InputText
            id="publish-offer-publisher-name"
            label={t('FullName')}
            value={data.publisherPartner?.name || ''}
            onChange={(value) =>
              actions.handleUpdatePublisherPartnerField('name', value)
            }
            required
            lgSize={false}
            tooltip={t('FullNameAsInIdentityDocument')}
          />
        </div>

        <div className="col-md-6 mb-3">
          <InputText
            id="publish-offer-publisher-cif"
            label={t('NifNie')}
            value={data.publisherPartner?.cif || ''}
            onChange={(value) =>
              actions.handleUpdatePublisherPartnerField('cif', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-md-6 mb-3">
          <InputText
            id="publish-offer-publisher-email"
            label={t('Email')}
            value={data.publisherPartner?.email || ''}
            onChange={(value) =>
              actions.handleUpdatePublisherPartnerField('email', value)
            }
            required
            lgSize={false}
            emailInput
          />
        </div>

        <div className="col-md-6 mb-3">
          <InputText
            id="publish-offer-publisher-email-subsidiary"
            label={t('EmailSubsidiary')}
            value={data.emailSubsidiary}
            onChange={actions.handleUpdateEmailSubsidiary}
            lgSize={false}
            emailInput
          />
        </div>

        <div className="col-md-6 mb-3">
          <InputText
            id="publish-offer-publisher-phone"
            label={t('Telephone')}
            value={data.publisherPartner?.phone || ''}
            onChange={(value) =>
              actions.handleUpdatePublisherPartnerField('phone', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-12 my-2">
          <h6 className="overline-title text-soft">{t('FiscalResidence')}</h6>
        </div>

        <div className="col-12 mb-3">
          <InputText
            id="publish-offer-publisher-address-line1"
            label={t('FullAddress')}
            value={partnerAddress?.line1 || ''}
            onChange={(value) =>
              actions.handleUpdatePublisherPartnerAddressField('line1', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-md-4 mb-3">
          <InputText
            id="publish-offer-publisher-address-zip"
            label={t('PostalCode')}
            value={partnerAddress?.zip || ''}
            onChange={(value) =>
              actions.handleUpdatePublisherPartnerAddressField('zip', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-md-4 mb-3">
          <InputText
            id="publish-offer-publisher-address-city"
            label={t('City')}
            value={partnerAddress?.city || ''}
            onChange={(value) =>
              actions.handleUpdatePublisherPartnerAddressField('city', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-md-4 mb-3">
          <InputText
            id="publish-offer-publisher-address-country"
            label={t('Country')}
            value={partnerAddress?.country || ''}
            onChange={(value) =>
              actions.handleUpdatePublisherPartnerAddressField('country', value)
            }
            required
            lgSize={false}
          />
        </div>

        <div className="col-12 my-2">
          <h6 className="overline-title text-soft">{t('BankInformation')}</h6>
        </div>

        <div className="col-12 mb-3">
          <InputText
            id="publish-offer-publisher-bank-account"
            label={t('BankAccountNumber')}
            value={data.publisherPartner?.bankAccountNumber || ''}
            onChange={handleBankAccountChange}
            onBlur={handleBankAccountBlur}
            required
            lgSize={false}
            additionalInfo={ibanError || undefined}
            additionalInfoType={ibanError ? MessageType.ERROR : undefined}
          />
        </div>

        <div className="col-12 my-2">
          <h6 className="overline-title text-soft">{t('Documentation')}</h6>
        </div>

        <div className="col-12">
          <div className="form-group">
            <label className="form-label d-flex align-items-center">
              {t('AttachIdentityDocument')}
              <MandatoryField className="ml-1" />
              <IconTooltip
                id="publish-offer-identity-doc-tooltip"
                tooltip={t('IdentityDocumentTooltip')}
                place="right"
                className="fs-24px text-soft"
              />
            </label>
            <div
              className="upload-zone border border-dashed rounded p-4 text-center dropzone dz-clickable"
              role="button"
              tabIndex={0}
              onClick={handleUploadClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleUploadClick();
                }
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <em className="icon ni ni-upload-cloud fs-24px text-soft" />
              <p className="text-soft mb-2">{t('DragDocumentHereOr')}</p>
              <button
                type="button"
                className="btn btn-dim btn-sm btn-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUploadClick();
                }}
              >
                {t('SelectFile')}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="d-none"
                accept={ACCEPTED_FILE_TYPES}
                onChange={handleFileSelect}
                multiple={false}
              />
              <p className="text-soft fs-11px mt-2 mb-0">
                {t('IdentityDocumentFormats')}
              </p>
            </div>

            {data.publisherDocuments.length > 0 && (
              <div className="mt-2">
                {data.publisherDocuments.map((file, index) => (
                  <div
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    className="d-flex align-items-center justify-content-between py-1 px-2 bg-light rounded mb-1"
                  >
                    <span className="text-soft fs-12px text-truncate flex-grow-1 mr-2">
                      {file.name}
                    </span>
                    <button
                      type="button"
                      className="btn btn-icon btn-sm btn-outline-danger"
                      onClick={() =>
                        actions.handleRemovePublisherDocument(index)
                      }
                      aria-label={t('Remove')}
                    >
                      <em className="icon ni ni-trash" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="col-12 mt-2">
          {isSellOffer && (
            <InputCheckbox
              id="publish-offer-hide-seller-name-checkbox"
              label={t('DoNotShowSellerName')}
              checked={data.isHideSellerName}
              onChange={actions.handleChangeHideSellerName}
              marginBottom={2}
            />
          )}

          <div className="custom-control custom-checkbox mb-2">
            <input
              type="checkbox"
              className="custom-control-input"
              id="publish-offer-accept-terms-checkbox"
              checked={data.isTermsAndConditionsAccepted}
              onChange={actions.handleChangeTermsAndConditionsAcceptance}
            />
            <label
              className="custom-control-label"
              htmlFor="publish-offer-accept-terms-checkbox"
            >
              {t('ConfirmDataAndAcceptTerms')}{' '}
              <button
                type="button"
                className="btn btn-link p-0 align-baseline"
                onClick={handleShowTerms}
              >
                {t('EquityMarketTermsAndConditions')}
              </button>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublishTradingOfferModalStep2;
